import React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import {
  LocationOn,
  MapOutlined,
  EditOutlined,
  ChevronLeft,
  AddCircleOutline,
} from "@mui/icons-material";
import { CustomTextField } from "../../../../components/formsComponents";
import { Dialog } from "../../../../components/Dialogs/Dialog";
import { CountrySelector } from "../../../../components/CountrySelector/CountrySelector";
import { EnrTimeZoneSelector } from "../../../../components/CountrySelector/EnrTimeZoneSelector";
import { COUNTRIES } from "../../../../constants/countries";
import { ILocationResponsePayload } from "../../../../components/LocationAutoComplete/LocationAutoComplete";
import {
  MapPicker,
  IAddressDetails,
} from "../../../Chatbot/components/MapPicker";
import { FieldLabel } from "../components/FieldLabel";
import { ACCENT } from "../BusinessOnboard.constant";
import { IFormikBridge } from "../BusinessOnboard.types";

interface ICompanyStepProps extends IFormikBridge {
  onLocationChange: (data: ILocationResponsePayload) => void;
  /** True once the user pressed Continue; forces the form into view + errors. */
  submitAttempted?: boolean;
}

type Mode = "choose" | "form";

/**
 * Step 1 — Banquet/company details. First shows a method chooser (map vs
 * manual). "Choose from map" opens the MapPicker and, on selection, reveals
 * the (pre-filled) address form; "Enter manually" reveals the empty form.
 */
export const CompanyStep: React.FC<ICompanyStepProps> = ({
  values,
  errors,
  handleChange,
  setFieldValue,
  onLocationChange,
  submitAttempted,
}) => {
  const [mode, setMode] = React.useState<Mode>("choose");
  const [mapOpen, setMapOpen] = React.useState(false);
  const [postalOpen, setPostalOpen] = React.useState(false);
  const [postalDraft, setPostalDraft] = React.useState("");

  const openPostalDialog = () => {
    setPostalDraft(values.company_outlet_address_postal_code || "");
    setPostalOpen(true);
  };

  const savePostal = () => {
    setFieldValue("company_outlet_address_postal_code", postalDraft.trim());
    setPostalOpen(false);
  };

  // If the user pressed Continue while still on the chooser, show the form so
  // they can see and fix the required-field errors.
  React.useEffect(() => {
    if (submitAttempted) setMode("form");
  }, [submitAttempted]);

  // ISO of the currently selected company country (from the form). Used to
  // scope the timezone options and the map picker's location search.
  const selectedCountryIso =
    COUNTRIES.find((c) => c.name === values.company_outlet_address_country)
      ?.code || "IN";


  const handleMapSelect = (_place: any, addressDetails?: IAddressDetails) => {
    if (addressDetails) {
      onLocationChange({
        place: addressDetails.place || "",
        country: addressDetails.country || "",
        address_line1:
          addressDetails.address_line1 || addressDetails.address || "",
        address_line2: addressDetails.address_line2 || "",
        city: addressDetails.city || "",
        state: addressDetails.state || "",
        postal_code: addressDetails.pin_code || "",
        region: addressDetails.region || "IND",
      });
      // Show the (now pre-filled) form to review/edit.
      setMode("form");
    }
    setMapOpen(false);
  };

  /* ---------- Method chooser (two cards) ---------- */
  if (mode === "choose") {
    const cards = [
      {
        icon: <MapOutlined sx={{ fontSize: 30 }} />,
        title: "Choose from map",
        subtitle: "Search and pick your location",
        onClick: () => setMapOpen(true),
      },
      {
        icon: <EditOutlined sx={{ fontSize: 30 }} />,
        title: "Enter manually",
        subtitle: "Type your address details",
        onClick: () => setMode("form"),
      },
    ];

    return (
      <>
        <Grid container spacing={2}>
          {cards.map((card) => (
            <Grid item xs={12} sm={6} key={card.title}>
              <Box
                onClick={card.onClick}
                sx={{
                  height: "100%",
                  p: 2.5,
                  borderRadius: "14px",
                  border: "1px solid",
                  borderColor: "grey.200",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 1,
                  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                  "&:hover": {
                    borderColor: ACCENT.main,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                    "& .card-icon": { color: ACCENT.main },
                  },
                }}
              >
                <Box className="card-icon" sx={{ color: "grey.600" }}>
                  {card.icon}
                </Box>
                <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
                  {card.title}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.85rem", color: "text.secondary" }}
                >
                  {card.subtitle}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {mapOpen && (
          <MapPicker
            open={mapOpen}
            onClose={() => setMapOpen(false)}
            onHandleLocationSelect={handleMapSelect}
            variant="error"
            defaultCountryIso={selectedCountryIso}
          />
        )}
      </>
    );
  }

  /* ---------- Address form ---------- */
  return (
    <Stack spacing={1.75}>
      {/* Change method + choose from map */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          role="button"
          onClick={() => setMode("choose")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            cursor: "pointer",
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          <ChevronLeft fontSize="small" />
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
            Change method
          </Typography>
        </Box>
        <Box
          role="button"
          onClick={() => setMapOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            color: ACCENT.main,
            "&:hover": { color: ACCENT.dark },
          }}
        >
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, lineHeight: 1 }}>
            Choose from map
          </Typography>
          <LocationOn fontSize="small" />
        </Box>
      </Box>

      <Box>
        <FieldLabel label="Banquet name" required />
        <CustomTextField
          placeholder="e.g. Grand Palace Banquet"
          fullWidth
          name="company_outlet_name"
          value={values.company_outlet_name}
          onChange={handleChange}
          error={!!errors.company_outlet_name}
          helperText={errors.company_outlet_name}
        />
      </Box>

      <Box>
        <Grid container spacing={1.5} alignItems="flex-start">
          <Grid item xs={12} sm={6}>
            <FieldLabel label="Address line 1" required />
            <CustomTextField
              placeholder="Street address"
              fullWidth
              name="company_outlet_address_line1"
              value={values.company_outlet_address_line1}
              onChange={handleChange}
              error={!!errors.company_outlet_address_line1}
              helperText={errors.company_outlet_address_line1}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel label="Address line 2" />
            <CustomTextField
              placeholder="Area or landmark"
              fullWidth
              name="company_outlet_address_line2"
              value={values.company_outlet_address_line2}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel label="City" required />
            <CustomTextField
              placeholder="City"
              fullWidth
              name="company_outlet_address_city"
              value={values.company_outlet_address_city}
              onChange={handleChange}
              error={!!errors.company_outlet_address_city}
              helperText={errors.company_outlet_address_city}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel label="State / Province" required />
            <CustomTextField
              placeholder="State"
              fullWidth
              name="company_outlet_address_state_or_province"
              value={values.company_outlet_address_state_or_province}
              onChange={handleChange}
              error={!!errors.company_outlet_address_state_or_province}
              helperText={errors.company_outlet_address_state_or_province}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "1.1rem",
                mb: 0.75,
              }}
            >
              <FieldLabel label="Country" required noMargin />
              <Box
                role="button"
                onClick={openPostalDialog}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  color: values.company_outlet_address_postal_code
                    ? "text.primary"
                    : ACCENT.main,
                  "&:hover": { color: ACCENT.dark },
                }}
              >
                <Typography
                  sx={{ fontSize: "0.72rem", fontWeight: 700, lineHeight: 1 }}
                >
                  {values.company_outlet_address_postal_code
                    ? `Postal Code: ${values.company_outlet_address_postal_code}`
                    : "Add postal code"}
                </Typography>
                {values.company_outlet_address_postal_code ? (
                  <EditOutlined sx={{ fontSize: 15 }} />
                ) : (
                  <AddCircleOutline sx={{ fontSize: 15 }} />
                )}
              </Box>
            </Box>
            <CountrySelector
              fullWidth
              value={
                COUNTRIES.find(
                  (c) => c.name === values.company_outlet_address_country,
                )?.code || null
              }
              onChange={(country) =>
                setFieldValue("company_outlet_address_country", country.name)
              }
              error={!!errors.company_outlet_address_country}
              helperText={errors.company_outlet_address_country}
              sx={{
                "&.MuiOutlinedInput-root": {
                  minHeight: 36.39,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel label="Timezone" required />
            <EnrTimeZoneSelector
              fullWidth
              countryCode={
                COUNTRIES.find(
                  (c) => c.name === values.company_outlet_address_country,
                )?.code || null
              }
              value={values.company_outlet_timezone}
              onChange={(tz) =>
                setFieldValue("company_outlet_timezone", tz.value)
              }
              error={errors.company_outlet_timezone}
              sx={{
                "&.MuiOutlinedInput-root": {
                  minHeight: 36.39,
                  // borderRadius: "10px",
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {mapOpen && (
        <MapPicker
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          onHandleLocationSelect={handleMapSelect}
          variant="error"
          defaultCountryIso={selectedCountryIso}
        />
      )}

      <Dialog
        variant="error"
        open={postalOpen}
        onClose={() => setPostalOpen(false)}
        size="xs"
        title="Postal code"
        subtitle="Enter your postal / ZIP code."
        actions={[
          {
            type: "button",
            variant: "text",
            label: "Cancel",
            onClick: () => setPostalOpen(false),
          },
          {
            type: "button",
            variant: "contained",
            label: "Save",
            color: "error",
            onClick: savePostal,
          },
        ]}
      >
        <Box sx={{ pt: 1 }}>
          <FieldLabel label="Postal code" />
          <CustomTextField
            placeholder="e.g. 411028"
            fullWidth
            autoFocus
            value={postalDraft}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
              setPostalDraft(digits);
            }}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                savePostal();
              }
            }}
          />
        </Box>
      </Dialog>
    </Stack>
  );
};
